import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { AlertTriangleIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCreatorsTableContext } from "../CreatorsTableContext";
import { IssuesFilterProps } from "../types";

/** The issue kinds the filter offers — same keys as `issuesFilterFn` and the
 *  org-wide facets (`backend/services/org_creators.py` ISSUE_CLAUSES). */
const ISSUE_OPTIONS: { value: string; idSuffix: string; label: string }[] = [
  { value: "error", idSuffix: "error", label: "Has Error" },
  { value: "hand_off", idSuffix: "handoff", label: "Hand-off to User" },
  { value: "not_autopilot", idSuffix: "autopilot", label: "Not on Autopilot" },
  { value: "contract_change", idSuffix: "contract-change", label: "Contract Change" },
];

export default function IssuesFilter({ id, data }: IssuesFilterProps) {
  const { table, serverFacets } = useCreatorsTableContext();

  // Counts over the loaded rows — or org-wide, from the server facets (one
  // page can't count the whole org).
  const issueCounts = useMemo((): Record<string, number> => {
    if (serverFacets) return serverFacets.issues;
    return {
      error: data.filter((c) => c.error && c.error !== "").length,
      hand_off: data.filter((c) => c.hand_off_to_user === true).length,
      not_autopilot: data.filter((c) => c.autopilot === false).length,
      contract_change: data.filter((c) => c.eve_contract_change?.status === "pending").length,
    };
  }, [data, serverFacets]);

  const selectedIssues = useMemo(() => {
    const filterValue = table.getColumn("issues")?.getFilterValue() as string[];
    return filterValue ?? [];
  }, [table.getColumn("issues")?.getFilterValue()]);

  const handleIssueFilter = (checked: boolean, value: string) => {
    const filterValue = table.getColumn("issues")?.getFilterValue() as string[];
    const newFilterValue = filterValue ? [...filterValue] : [];

    if (checked) {
      newFilterValue.push(value);
    } else {
      const index = newFilterValue.indexOf(value);
      if (index > -1) {
        newFilterValue.splice(index, 1);
      }
    }

    table
      .getColumn("issues")
      ?.setFilterValue(newFilterValue.length ? newFilterValue : undefined);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" className="h-7.5 px-2 hover:bg-muted-foreground/10 data-[state=open]:bg-muted data-[state=open]:[&>svg]:opacity-100">
          <AlertTriangleIcon
            className="opacity-60"
            size={16}
            aria-hidden="true"
          />
          Issues
          {selectedIssues.length > 0 && (
            <Badge variant="count" className="-me-1">
              {selectedIssues.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-36 p-1"
        align="start"
      >
        <div className="space-y-2">
          <div className="text-muted-foreground text-xs font-medium p-2 pb-0">
            Filters
          </div>
          <div className="">
            {ISSUE_OPTIONS.map((option) => {
              const checked = selectedIssues.includes(option.value);
              return (
                <div
                  key={option.value}
                  className="flex items-center gap-2 p-2 pr-3 hover:bg-muted rounded-sm cursor-pointer"
                  onClick={() => handleIssueFilter(!checked, option.value)}
                >
                  <Checkbox
                    id={`${id}-issue-${option.idSuffix}`}
                    checked={checked}
                    onCheckedChange={(next: boolean) =>
                      handleIssueFilter(next, option.value)
                    }
                  />
                  <Label className="flex grow justify-between gap-2 font-normal cursor-pointer text-xs">
                    {option.label}{" "}
                    <span className="text-muted-foreground ms-2">
                      {issueCounts[option.value] ?? 0}
                    </span>
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
